import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from '../../Landing/theme-provider';
import Navigation from '../components/Navigation';
import PricingHero from './components/PricingHero';
import PricingCardsExtended from './components/PricingCardsExtended';
import FounderStory from './components/FounderStory';

import BenefitsDeepDive from './components/BenefitsDeepDive';
import SocialProof from './components/SocialProof';
import ComprehensiveFAQ from '../components/ComprehensiveFAQ';
import Footer from '../../Landing/footer';
import './PricingPage.css';

const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  color: #0f1116;
  background: linear-gradient(180deg, #f3efff 0%, #ffffff 600px);
`;

const Main = styled.main`
  margin-top: -70px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;

  @media (max-width: 968px) {
    margin-top: -72px;
  }
`;

const Section = styled.div`
  position: relative;
`;

const GettingStarted = styled.section`
  padding: 88px 24px;
  background: #ffffff;
`;

const GettingStartedInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const GettingStartedHeader = styled.div`
  max-width: 660px;
  margin: 0 auto 36px;
  text-align: center;

  span {
    color: #6d4aff;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: .16em;
    text-transform: uppercase;
  }

  h2 {
    margin: 14px 0 12px;
    color: #0f1116;
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(2rem, 4vw, 2.65rem);
    font-weight: 700;
    letter-spacing: -.04em;
    line-height: 1;
  }

  p {
    margin: 0;
    color: #5f5a70;
    font-size: 17px;
    line-height: 1.6;
  }
`;

const GettingStartedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const GettingStartedCard = styled.article`
  padding: 28px;
  background: #ffffff;
  border: 1px solid #e2e0dc;
  border-radius: 16px;

  span {
    display: inline-grid;
    width: 30px;
    height: 30px;
    place-items: center;
    margin-bottom: 18px;
    color: #ffffff;
    background: #2d3647;
    border-radius: 50%;
    font-size: 13px;
    font-weight: 700;
  }

  h3 {
    margin: 0 0 8px;
    color: #0f1116;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 20px;
  }

  p {
    margin: 0;
    color: #666a72;
    font-size: 15px;
    line-height: 1.6;
  }
`;

const GettingStartedSection: React.FC = () => (
  <GettingStarted>
    <GettingStartedInner>
      <GettingStartedHeader>
        <span>Getting started</span>
        <h2>From first conversation to live.</h2>
        <p>Every paid plan is designed to help you launch a useful AI concierge without a drawn-out setup process.</p>
      </GettingStartedHeader>
      <GettingStartedGrid>
        <GettingStartedCard>
          <span>01</span>
          <h3>Request a custom demo</h3>
          <p>See how SmushChat can work with your website and the questions your customers already ask.</p>
        </GettingStartedCard>
        <GettingStartedCard>
          <span>02</span>
          <h3>Choose your plan</h3>
          <p>Pick the usage limits that fit today, knowing you can move up as your business grows.</p>
        </GettingStartedCard>
        <GettingStartedCard>
          <span>03</span>
          <h3>Launch with support</h3>
          <p>Setup and onboarding are included with every paid plan, so you are never left figuring it out alone.</p>
        </GettingStartedCard>
      </GettingStartedGrid>
    </GettingStartedInner>
  </GettingStarted>
);

const PricingPage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
            <Page className="pricing-page">
                <Helmet>
                    <title>SmushChat Pricing: AI Concierge Plans | SmushLabs</title>
                    <meta name="description" content="Compare paid SmushChat AI concierge plans. Setup and onboarding are included, and you can request a free custom demo before purchasing." />
                    <meta name="keywords" content="ai chatbot pricing, chatbot pricing plans, affordable chatbot software, chatbot subscription, ai customer support pricing, lead generation chatbot, conversational ai pricing, chatbot cost, small business chatbot pricing, startup chatbot plans" />

                    {/* Open Graph / Social Media */}
                    <meta property="og:title" content="SmushChat Pricing: AI Concierge Plans" />
                    <meta property="og:description" content="Compare paid plans, with setup and onboarding included. Request a free custom demo before purchasing." />
                    <meta property="og:type" content="website" />
                    <meta property="og:url" content="https://smushlabs.ai/pricing" />

                    {/* Twitter Card */}
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content="SmushChat Pricing: AI Concierge Plans" />
                    <meta name="twitter:description" content="Compare paid plans and request a free custom demo before purchasing." />

                    <link rel="canonical" href="https://smushlabs.ai/pricing" />
                </Helmet>

                <Navigation />

                <Main>
                    <PricingHero />

                    <Section id="plans">
                        <PricingCardsExtended />
                    </Section>

                    <SocialProof />

                    <BenefitsDeepDive />

                    <GettingStartedSection />

                    <FounderStory />

                    <ComprehensiveFAQ />

                    <Footer />
                </Main>
            </Page>
        </ThemeProvider>
    );
};

export default PricingPage;
